import classNames from 'classnames';
import { getKeys } from 'shared/lib/object';

import type { OnboardingModel } from '../model';

import { ProgressBar } from './ProgressBar';
import { CreditCardCompleted } from './Steps/CreditCardCompleted';
import { GenericStep } from './Steps/GenericStepCompleted';
import { getProgressBarSections } from './get-progress-bar-sections';
import { stepsMap } from './constants';
import { HolidayStepCompleted } from './Steps/HolidayStepCompleted';

interface OnboardingCampaignProps {
  className?: string;
  refetchOnboarding: () => void;
  onboardingData: OnboardingModel | undefined;
}

const ONBOARDING_PROGRESS =
  'Your manager is uploading all your data, please make sure to provide all information below to start your first campaign.';
const ONBOARDING_DONE =
  'Everything is ready to start receiving quality leads. Your manager will contact you to confirm your \ncampaign start and will be your main contact moving forward!';

export function OnboardingCampaign(props: OnboardingCampaignProps) {
  const { className, refetchOnboarding, onboardingData } = props;

  if (onboardingData?.meta.completed || !onboardingData) {
    return <></>;
  }

  const sections = getProgressBarSections(onboardingData.steps);
  const steps = getKeys(onboardingData.steps).map((key) => {
    return { ...stepsMap[key], status: onboardingData.steps[key] };
  });

  const onboardingComplete = onboardingData.meta.completionPercentage > 99;
  const ONBOARDING_TEXT = onboardingComplete ? ONBOARDING_DONE : ONBOARDING_PROGRESS;
  const ONBOARDING_HEADER = onboardingComplete
    ? 'Onboarding process is complete!'
    : `Onboarding process for ${onboardingData.meta.campaignName}!`;

  return (
    <div className={classNames('rounded-md border border-slate-100 py-11', className)}>
      <div className="mb-7 text-center">
        <h2 className="mb-2 text-xl font-medium">{ONBOARDING_HEADER}</h2>
        <span className="whitespace-pre-wrap text-sm font-normal">{ONBOARDING_TEXT}</span>
      </div>
      <ProgressBar
        sections={sections}
        percentage={onboardingData.meta.completionPercentage}
      />
      <div className="mt-5 flex flex-col items-center">
        {steps.map(({ component, status, text, tooltipText, iconName, optional }, idx) => {
          if (!status) {
            return <></>;
          }
          if (component === 'CreditCardCompleted') {
            return (
              <CreditCardCompleted
                key={text}
                status={status}
                refetchOnboarding={refetchOnboarding}
              />
            );
          }

          if (component === 'HolidayStepCompleted') {
            return (
              <HolidayStepCompleted
                key={text}
                status={status}
                text={text}
                iconName={iconName}
                tooltipText={tooltipText[status]}
                optional={optional}
                isLast={idx + 1 === steps.length}
              />
            );
          }

          return (
            <GenericStep
              key={text}
              status={status}
              text={text}
              iconName={iconName}
              tooltipText={tooltipText[status]}
              isLast={idx + 1 === steps.length}
            />
          );
        })}
      </div>
    </div>
  );
}
